import * as React from "react";
import styled from 'styled-components';
import Chart from 'react-apexcharts';

import Button from 'src/SharedComponents/Button';
import Dialog from 'src/SharedComponents/Dialog/Dialog';
import DialogActions from 'src/SharedComponents/Dialog/DialogActions';
import DialogContent from 'src/SharedComponents/Dialog/DialogContent';
import DialogTitle from 'src/SharedComponents/Dialog/DialogTitle';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import ViewContainer from 'src/SharedComponents/ViewContainer';
import ManagerTitleBar from 'src/Views/Manager/ManagerTitleBar';

import createErrorMessage from 'src/UsefulFunctions/createErrorMessage';
import * as agentManagementService from "./agentManagementService";
import IAgent from 'src/ServerEntities/IAgent';
import IAgentMetric from 'src/ServerEntities/IAgentMetric';

const ButtonContainer = styled.div`
    display: flex;
    gap: 8px;
`;

const DetailsContainer = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`;

const InfoRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 32px;
`;

const DetailRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DetailLabel = styled.span`
    font-size: 11px;
    text-transform: uppercase;
    color: #888;
    font-weight: 600;
    letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
    font-size: 14px;
    color: #333;
    word-break: break-all;
`;

const ChartContainer = styled.div`
    display: flex;
    flex-direction: column;
    background: #1c3e5a;
    border-radius: 8px;
    flex: 1;
    min-height: 0;

    & h3 {
        background-color: #246896;
        margin: 0;
        padding: 12px 16px 6px;
        border-radius: 8px 8px 0 0;
        font-weight: 500;
        font-size: 1rem;
        color: #fff;
        flex-shrink: 0;
    }
`;

interface IAgentDetailsProps {
    selectedAgent: IAgent;
    webToken: string;
    refreshAgents: () => void;
    setErrorMessage: (errorMessage: string) => void;
}

const AgentDetails = (props: IAgentDetailsProps) => {
    const { webToken, selectedAgent, refreshAgents, setErrorMessage } = props;
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [metrics, setMetrics] = React.useState([] as IAgentMetric[]);
    const [metricsLoading, setMetricsLoading] = React.useState(false);

    React.useEffect(() => {
        setMetricsLoading(true);
        agentManagementService.getAgentMetrics(
            selectedAgent.id,
            webToken,
            (data) => { setMetrics(data); setMetricsLoading(false); },
            (_err) => { setMetricsLoading(false); }
        );
    }, [selectedAgent.id]);

    const enableAgent = () => {
        setErrorMessage("");
        agentManagementService.enableAgent(
            selectedAgent.id,
            webToken,
            () => { refreshAgents(); },
            (serverError: string) => {
                setErrorMessage(createErrorMessage("enabling agent", serverError));
            }
        );
    };

    const disableAgent = () => {
        setErrorMessage("");
        agentManagementService.disableAgent(
            selectedAgent.id,
            webToken,
            () => { refreshAgents(); },
            (serverError: string) => {
                setErrorMessage(createErrorMessage("disabling agent", serverError));
            }
        );
    };

    const deleteAgent = () => {
        setErrorMessage("");
        agentManagementService.deleteAgent(
            selectedAgent.id,
            webToken,
            () => { refreshAgents(); },
            (serverError: string) => {
                setErrorMessage(createErrorMessage("deleting agent", serverError));
            }
        );
        setShowDeleteConfirm(false);
    };

    const chartOptions = {
        chart: {
            foreColor: "#ffffff",
            toolbar: { show: false }
        },
        xaxis: { type: "datetime" as const },
        tooltip: { theme: "dark" },
        colors: ["#42a8c3", "#ff2121"]
    };

    const chartSeries = [
        {
            name: "Messages delivered",
            data: metrics.map(m => ({ x: m.period, y: m.count }))
        },
        {
            name: "Messages dropped",
            data: metrics.map(m => ({ x: m.period, y: m.dropped }))
        }
    ];

    return <ViewContainer>
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} darkBackground={true}>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogContent style={{ lineHeight: "25px" }}>
                Are you sure you want to delete agent <strong>{selectedAgent.name}</strong>? This action cannot be undone.
            </DialogContent>
            <DialogActions>
                <Button outlined={true} onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button color={"#ad0000"} onClick={deleteAgent}>Delete</Button>
            </DialogActions>
        </Dialog>
        <ManagerTitleBar viewName={selectedAgent.name} viewDescription={`Client ID: ${selectedAgent.clientId}`}>
            <ButtonContainer>
                {!selectedAgent.status && (
                    <Button color={"#4f7326"} onClick={enableAgent}>Enable agent</Button>
                )}
                {selectedAgent.status && (
                    <Button color={"#ad0000"} onClick={disableAgent}>Disable agent</Button>
                )}
                <Button color={"#ad0000"} onClick={() => setShowDeleteConfirm(true)}>Delete agent</Button>
            </ButtonContainer>
        </ManagerTitleBar>
        <DetailsContainer>
            <InfoRow>
                <DetailRow>
                    <DetailLabel>Name</DetailLabel>
                    <DetailValue>{selectedAgent.name}</DetailValue>
                </DetailRow>
                <DetailRow>
                    <DetailLabel>Status</DetailLabel>
                    <DetailValue>{selectedAgent.status ? "Enabled" : "Disabled"}</DetailValue>
                </DetailRow>
            </InfoRow>
            <ChartContainer>
                <h3>Message throughput</h3>
                <LoadingIndicator type="Linear" show={metricsLoading} />
                <Chart
                    type="area"
                    height="90%"
                    options={chartOptions}
                    series={chartSeries}
                />
            </ChartContainer>
        </DetailsContainer>
    </ViewContainer>;
};

export default AgentDetails;
